// store/modules/demographics.ts
import {
  APIFetchDemographicsByHour,
  APIFetchDemographicsByPeriod,
  APIFetchDemographicsByStore,
  APIFetchDemographicsByWeek,
  APIFetchDemographicsDistributionByGroup,
  APIFetchDemographicsFidaAll
} from '@/api/demographics';
import store from '@/store/';
import {
  getStoreMinAndMaxHourForDatesAndStores,
  getDateRange
} from '@/util/utilFidaFunctions';

import { groupNamesList } from '@/util/mockData/groupName';

interface IInitialState {
  kpiMetrics: any[];
  demoGroup: any[];
  demoGender: any[];
  demoHourly: any[];
  demoPeriod: any[];
  demoWeekly: any[];
  demoPeriodValue: any[];
  demoGenderValue: any[];
  demoGroupValue: any[];
  demographicsDmg: any[];
  groupMetricKey: any;
}

const initialState: IInitialState = {
  kpiMetrics: [],
  demoGroup: [],
  demoGender: [],
  demoHourly: [],
  demoWeekly: [],
  demoPeriod: [],
  demoPeriodValue: [],
  demoGenderValue: [],
  demoGroupValue: [],
  groupMetricKey: false,
  demographicsDmg: []
};

export default {
  state: initialState,
  mutations: {
    setDemographicsByPeriod(state, payload) {
      state.demoPeriod = payload;
    },
    setDemographicsDmg(state, payload) {
      state.demographicsDmg = payload;
    },
    setDemographicsByPeriodValue(state, payload) {
      state.demoPeriodValue = payload;
    },
    setDemographicsByWeek(state, payload) {
      state.demoWeekly = payload;
    },
    setDemographicsByHour(state, payload) {
      state.demoHourly = payload;
    },
    setDemographicsDistributionByGroup(state, payload) {
      state.demoGroup = payload;
    },
    setDemographicsDistributionByGroupValue(state, payload) {
      state.demoGroupValue = payload;
    },
    setDemographicsByGender(state, payload) {
      state.demoGender = payload;
    },
    setDemographicsByGenderValue(state, payload) {
      state.demoGenderValue = payload;
    },
    setDemographicsByStore(state, payload) {
      state.kpiMetrics = payload;
    }
  },
  actions: {
    // Fetch all (Fida) - works with kpis.daily / kpis.hourly + dmg
    async fetchDemographicsFidaAll(
      { commit, rootState },
      { storeCodes, startDate, endDate, type, frequency = 'daily' }
    ) {
      if (type === 'weekly') {
        frequency = 'daily';
      }

      const res = await APIFetchDemographicsFidaAll({
        storeCodes,
        startDate,
        endDate,
        frequency,
        typeD: 'byPeriod'
      });

      const responseDmg = res?.dmg ?? [];
      const kpis = res?.kpis ?? {};
      const stores = res?.stores ?? {};
      commit('setDemographicsDmg', responseDmg);

      // helper to get period array based on frequency
      const periodArray: Array<any> =
        frequency === 'daily' ? kpis.daily ?? [] : kpis.hourly ?? [];

      // ---- Distribution by Group (totals across daily periodArray if present; fall back to hourly)
      // Sum per groupId across periodArray
      const groupTotals: Record<string, number> = {};
      (periodArray || []).forEach((item: any) => {
        const dm = item?.dm ?? {};
        Object.keys(dm).forEach(k => {
          const v = Number(dm[k]) || 0;
          groupTotals[k] = (groupTotals[k] || 0) + v;
        });
      });
      const finalResponseGroup = groupNamesList.map(group => {
        const gid = String(group.DGGroupID);
        return {
          group: group.DGGroup_JP,
          groupEN: group.DGGroup_EN,
          count: groupTotals[gid] || 0,
          color: group.DGGroupDisplayColor
        };
      });

      // same for group value (kept same as totals here)

      const finalResponseGroupValue = finalResponseGroup.map(g => ({ ...g }));
      const id = rootState.user.profile.tenant._id;

      // Declare variables first (so they are available outside the if/else)
      let maleGroupIds = [];
      let femaleGroupIds = [];

      if (id === '4TxrckSM8PrcEvfx9') {
        maleGroupIds = groupNamesList.slice(0, 3).map(g => String(g.DGGroupID));
        femaleGroupIds = groupNamesList
          .slice(3, 6)
          .map(g => String(g.DGGroupID));
      } else {
        maleGroupIds = groupNamesList.slice(0, 4).map(g => String(g.DGGroupID));
        femaleGroupIds = groupNamesList
          .slice(4, 8)
          .map(g => String(g.DGGroupID));
      }

      // ---- Gender aggregates ----
      const maleTotal = Object.entries(groupTotals).reduce((s, [k, v]) => {
        return s + (maleGroupIds.includes(k) ? v : 0);
      }, 0);

      const femaleTotal = Object.entries(groupTotals).reduce((s, [k, v]) => {
        return s + (femaleGroupIds.includes(k) ? v : 0);
      }, 0);

      const finalResponseByGender = [
        {
          count: maleTotal,
          color: '#007FBA',
          genderEN: 'Male',
          gender: '男性'
        },
        {
          count: femaleTotal,
          color: '#cc5a93',
          genderEN: 'Female',
          gender: '女性',
          genderJP: '女性'
        }
      ];

      const finalResponseByGenderValue = finalResponseByGender.map(g => ({
        ...g
      }));

      // ---- Build period data (finalResponse2 and finalResponse2Value)
      const buildPeriodMaps = (items: Array<any>, freq: 'daily' | 'hourly') => {
        const map: Record<string, Array<Record<string, any>>> = {};
        const mapValues: Record<string, Array<Record<string, any>>> = {};

        (items || []).forEach(item => {
          const dm = item?.dm ?? {};
          const timelineLabel = freq === 'daily' ? item.day : `${item.hour}:00`;

          Object.keys(dm).forEach(idStr => {
            const gid = Number(idStr);
            const groupNameObj = groupNamesList.find(g => g.DGGroupID === gid);
            if (!groupNameObj) return;
            const groupKey = groupNameObj.DGGroup;

            const count = Number(dm[idStr]) || 0;

            const entry = {
              demographicsGroup: groupNameObj.DGGroup_JP,
              demographicsGroupEN: groupNameObj.DGGroup_EN,
              count,
              timelineLabel,
              color: groupNameObj.DGGroupDisplayColor
            };

            map[groupKey] = map[groupKey] ? [...map[groupKey], entry] : [entry];
            mapValues[groupKey] = mapValues[groupKey]
              ? [...mapValues[groupKey], entry]
              : [entry];
          });
        });

        return { map, mapValues };
      };

      // choose source: prefer daily when frequency === 'daily', else hourly
      // choose source: prefer daily when frequency === 'daily', else hourly
      let sourceItems =
        frequency === 'daily' ? kpis.daily ?? [] : kpis.hourly ?? [];

      // ---- FIX: Ensure full hour range exists (11..19) ----
      if (frequency === 'hourly') {
        const storeState: any = store.state;
        const uniqueDates = getDateRange(startDate, endDate);

        // const storeCodesArr = storeState.store.storesCodeNameList.map(
        //   (s: any) => s.storeCode
        // );
         const storeCodesArr = stores.map(
          (s: any) => s.storeNumber
        );
       
        const resHours: any = getStoreMinAndMaxHourForDatesAndStores(
          uniqueDates,
          storeCodesArr
        );

        sourceItems = fillMissingHours(
          sourceItems,
          resHours.minOpening,
          resHours.maxClosing
        );
      }
      // ------------------------------------------------------

      // Build period maps from filled sourceItems
      const {
        map: finalResponse2,
        mapValues: finalResponse2Value
      } = buildPeriodMaps(sourceItems, frequency as 'daily' | 'hourly');

      // Apply grouping rules
      let desiredResponse;
      let desiredResponseValue;

      if (frequency === 'daily') {
        desiredResponse = getMissingDataByWeek(finalResponse2);
        desiredResponseValue = getMissingDataByWeek(finalResponse2Value);
      } else {
        // sorted by hour
        desiredResponse = getHourlySortedData(finalResponse2);
        desiredResponseValue = getHourlySortedData(finalResponse2Value);
      }

      // commits
      commit('setDemographicsDistributionByGroup', finalResponseGroup);
      commit(
        'setDemographicsDistributionByGroupValue',
        finalResponseGroupValue
      );
      commit('setDemographicsByGender', finalResponseByGender);
      commit('setDemographicsByGenderValue', finalResponseByGenderValue);
      commit('setDemographicsByPeriod', desiredResponse);
      commit('setDemographicsByPeriodValue', desiredResponseValue);
    },

    // Generic byPeriod fetch (hourly or daily)
    async fetchDemographicsByPeriod(
      { commit },
      { storeCodes, startDate, endDate, type, frequency = 'hourly' }
    ) {
      if (type === 'weekly') {
        frequency = 'daily';
      }

      const res = await APIFetchDemographicsFidaAll({
        storeCodes,
        startDate,
        endDate,
        frequency,
        typeD: 'byPeriod'
      });
  
      const kpis = res?.kpis ?? {};
      const stores = res?.stores ?? {};
      let items: Array<any> =
        frequency === 'daily' ? kpis.daily ?? [] : kpis.hourly ?? [];
      // if hourly and you want to fill missing hours between minOpening & maxClosing
      if (frequency === 'hourly') {
        // const storeState: any = store.state;
        const uniqueDates = getDateRange(startDate, endDate);

        // const storeCodesArr = storeState.store.storesCodeNameList.map(
        //   (s: any) => s.storeCode
        // );
        const storeCodesArr = stores.map(
          (s: any) => s.storeNumber
        );
        const resHours: any = getStoreMinAndMaxHourForDatesAndStores(
          uniqueDates,
          storeCodesArr
        );
        items = fillMissingHours(
          items,
          resHours.minOpening,
          resHours.maxClosing
        );
      }

      // Build finalResponse2 and finalResponse2Value from items
      const buildFromItems = (
        itemsArr: Array<any>,
        freq: 'daily' | 'hourly'
      ) => {
        const map: Record<string, Array<Record<string, any>>> = {};
        const mapValues: Record<string, Array<Record<string, any>>> = {};

        (itemsArr || []).forEach(item => {
          const dm = item?.dm ?? {};
          const timelineLabel = freq === 'daily' ? item.day : `${item.hour}:00`;

          // Loop over all groups, not just keys present in dm
          groupNamesList.forEach(group => {
            const groupKey = group.DGGroup;
            const count = Number(dm[String(group.DGGroupID)] ?? 0); // default 0 if missing

            const entry = {
              demographicsGroup: group.DGGroup_JP,
              demographicsGroupEN: group.DGGroup_EN,
              count,
              timelineLabel,
              color: group.DGGroupDisplayColor
            };

            map[groupKey] = map[groupKey] ? [...map[groupKey], entry] : [entry];
            mapValues[groupKey] = mapValues[groupKey]
              ? [...mapValues[groupKey], entry]
              : [entry];
          });
        });

        return { map, mapValues };
      };

      const {
        map: finalResponse2,
        mapValues: finalResponse2Value
      } = buildFromItems(items, frequency === 'daily' ? 'daily' : 'hourly');

      let desiredResponse;
      let desiredResponseValue;
      if (frequency === 'daily') {
        desiredResponse = getMissingDataByWeek(finalResponse2);
        desiredResponseValue = getMissingDataByWeek(finalResponse2Value);
      } else {
        desiredResponse = getHourlySortedData(finalResponse2);
        desiredResponseValue = getHourlySortedData(finalResponse2Value);
      }
      commit('setDemographicsByPeriodValue', desiredResponseValue);
      commit('setDemographicsByPeriod', desiredResponse);
    },

    // (left as stubs — unchanged from your original)
    async fetchDemographicsByWeek(
      { commit },
      { storeCodes, startDate, endDate }
    ) {
      let res = await APIFetchDemographicsByWeek({
        storeCodes,
        startDate,
        endDate
      });
      // no-op (original code)
    },

    async fetchDemographicsByHour(
      { commit },
      { storeCodes, startDate, endDate }
    ) {
      let res = await APIFetchDemographicsByHour({
        storeCodes,
        startDate,
        endDate
      });
      // no-op (original code)
    },

    async fetchDemographicsByStore(
      { commit },
      { storeCodes, startDate, endDate, kpis, daysOfWeek, groupByMetricKey }
    ) {
      let res = await APIFetchDemographicsByStore({
        storeCodes,
        startDate,
        endDate,
        kpis,
        daysOfWeek,
        groupByMetricKey
      });
      // handle as needed
    }
  }
}; // export default

// -------------------- helpers --------------------

const daysOfWeek = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday'
];

function getMissingDataByWeek(originalData: Record<string, any[]>) {
  const response: Record<string, any[]> = {};

  for (const [key, value] of Object.entries(originalData)) {
    const newData: any[] = [];

    for (const day of daysOfWeek) {
      const existingEntry = value.find(
        (item: any) => item.timelineLabel === day
      );
      const newEntry = {
        demographicsGroup: existingEntry
          ? existingEntry.demographicsGroup
          : key,
        demographicsGroupEN: existingEntry
          ? existingEntry.demographicsGroupEN
          : key,
        count: existingEntry ? existingEntry.count : 0,
        timelineLabel: day,
        color: existingEntry ? existingEntry.color : ''
      };
      newData.push(newEntry);
    }
    response[key] = newData;
  }
  return response;
}

/**
 * getHourlySortedData
 * - apiResponse: Record<groupKey, Array<{ timelineLabel: '9:00' | '10:00', ... }>>
 * - ensures arrays are sorted in ascending hour order
 */
function getHourlySortedData(
  apiResponse: Record<string, Array<Record<string, any>>>
) {
  for (const demographicsGroup in apiResponse) {
    if (Object.prototype.hasOwnProperty.call(apiResponse, demographicsGroup)) {
      const groupData = apiResponse[demographicsGroup];
      if (Array.isArray(groupData)) {
        groupData.sort((a, b) => {
          const parseTime = (s: string | number) => {
            // s could be '9:00' or '09:00' or number like 9
            if (typeof s === 'number') return s;
            const parts = String(s).split(':');
            return parseInt(parts[0], 10) || 0;
          };
          const timeA = parseTime(a.timelineLabel);
          const timeB = parseTime(b.timelineLabel);
          return timeA - timeB;
        });
      }
    }
  }
  return apiResponse;
}

/**
 * fillMissingHours
 * - data: array of hourly items { hour: number, dm: { "1": n, ... } }
 * - returns array that contains entries for every hour between minHour..maxHour (inclusive)
 * - missing hours are filled with dm keys set to 0
 */
function fillMissingHours(data: Array<any>, minHour: number, maxHour: number) {
  // Step 1: Sum dm values for same hour
  const summedByHour: Record<number, Record<string, number>> = {};

  (data || []).forEach(item => {
    const hour = item.hour;
    // console.log("item", item)
    if (typeof hour !== 'number') return;

    if (!summedByHour[hour]) summedByHour[hour] = {};

    for (const key in item.dm) {
      if (!summedByHour[hour][key]) summedByHour[hour][key] = 0;
      summedByHour[hour][key] += item.dm[key];
    }
  });

  // Step 2: Fill missing hours
  const filled: Array<any> = [];
  for (let h = minHour; h <= maxHour; h++) {
    const dmForHour = summedByHour[h];

    if (dmForHour) {
      filled.push({
        day: '', // unknown day after summing
        hour: h,
        dm: dmForHour
      });
    } else {
      // build zeroed dm for group ids present in groupNamesList
      const zeroDm: Record<string, number> = {};
      groupNamesList.forEach(g => {
        zeroDm[String(g.DGGroupID)] = 0;
      });

      filled.push({
        day: '', // unknown day for filler
        hour: h,
        dm: zeroDm
      });
    }
  }

  return filled;
}
